/**
 * Image Quality Assessment for Document Capture
 * Client-side quality checks before sending to backend
 */

export interface QualityMetrics {
  sharpness: number;
  contrast: number;
  brightness: number;
  isBlurry: boolean;
  hasGlare: boolean;
  overallScore: number;
}

export interface QualityFeedback {
  isValid: boolean;
  message: string;
  recommendations: string[];
  quality: QualityMetrics;
}

/**
 * Calculate image sharpness using Laplacian variance
 * Higher values = sharper image
 */
export function calculateSharpness(imageData: ImageData): number {
  const { width, height, data } = imageData;
  
  // Convert to grayscale
  const gray = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    gray[i] = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]);
  }
  
  // Apply Laplacian kernel
  const laplacian = new Float32Array(width * height);
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      
      // Laplacian kernel
      const val = 
        -1 * gray[idx - width] +
        -1 * gray[idx - 1] +
        4 * gray[idx] +
        -1 * gray[idx + 1] +
        -1 * gray[idx + width];
      
      laplacian[idx] = val;
      sum += val;
      sumSq += val * val;
      count++;
    }
  }
  
  // Calculate variance
  const mean = sum / count;
  const variance = (sumSq / count) - (mean * mean);
  
  return Math.abs(variance);
}

/**
 * Detect glare (overexposed areas)
 */
export function detectGlare(imageData: ImageData, threshold: number = 250): number {
  const { data } = imageData;
  let overexposed = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
    if (brightness > threshold) {
      overexposed++;
    }
  }
  
  return overexposed / (data.length / 4);
}

/**
 * Calculate contrast (standard deviation)
 */
export function calculateContrast(imageData: ImageData): number {
  const { data } = imageData;
  let sum = 0;
  let sumSq = 0;
  const pixelCount = data.length / 4;
  
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += gray;
    sumSq += gray * gray;
  }
  
  const mean = sum / pixelCount;
  const variance = (sumSq / pixelCount) - (mean * mean);
  
  return Math.sqrt(variance);
}

/**
 * Calculate brightness (mean pixel value)
 */
export function calculateBrightness(imageData: ImageData): number {
  const { data } = imageData;
  let sum = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
  }
  
  return sum / (data.length / 4);
}

/**
 * Full quality assessment.
 *
 * Threshold note: these cutoffs (sharpness<80, glare>15%, brightness
 * outside 50-200, contrast<30) are not calibrated against a specific input
 * resolution — Laplacian-variance sharpness in particular scales with image
 * size, so scoring a full-resolution capture vs. a downscaled copy can
 * shift the result. Caller (DocumentCapture._checkPhotoQuality) downscales
 * to a fixed 640px before calling this, so keep that pairing consistent —
 * if the downscale size changes, these thresholds should be re-checked
 * against real captures at the new size. This function is used as an
 * advisory-only nudge (never blocks submission), so a threshold that's
 * somewhat off costs an occasional wrong nudge, not a broken flow.
 */
export function assessQuality(imageData: ImageData): QualityFeedback {
  const sharpness = calculateSharpness(imageData);
  const glare = detectGlare(imageData);
  const contrast = calculateContrast(imageData);
  const brightness = calculateBrightness(imageData);
  
  const isBlurry = sharpness < 80;
  const hasGlare = glare > 0.15;
  const isTooDark = brightness < 50;
  const isTooBright = brightness > 200;
  const lowContrast = contrast < 30;
  
  // Calculate overall score (0-100)
  let score = 100;
  if (isBlurry) score -= 40;
  if (hasGlare) score -= 25;
  if (isTooDark) score -= 20;
  if (isTooBright) score -= 20;
  if (lowContrast) score -= 15;
  
  const metrics: QualityMetrics = {
    sharpness: Math.round(sharpness * 10) / 10,
    contrast: Math.round(contrast * 10) / 10,
    brightness: Math.round(brightness * 10) / 10,
    isBlurry,
    hasGlare,
    overallScore: Math.max(0, score),
  };
  
  // Generate feedback
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  if (isBlurry) {
    issues.push("Too blurry");
    recommendations.push("Hold camera steady");
    recommendations.push("Ensure good lighting");
  }
  
  if (hasGlare) {
    issues.push("Too much glare");
    recommendations.push("Avoid direct light on document");
    recommendations.push("Tilt document slightly");
  }
  
  if (isTooDark) {
    issues.push("Too dark");
    recommendations.push("Move to brighter area");
    recommendations.push("Turn on more lights");
  }
  
  if (isTooBright) {
    issues.push("Too bright");
    recommendations.push("Reduce lighting");
    recommendations.push("Move away from direct sun");
  }
  
  if (lowContrast) {
    issues.push("Low contrast");
    recommendations.push("Ensure even lighting");
  }
  
  const isValid = score >= 60;
  const message = issues.length > 0 
    ? issues.join(", ")
    : "Good quality";
  
  return {
    isValid,
    message,
    recommendations: recommendations.slice(0, 3), // Max 3 recommendations
    quality: metrics,
  };
}

/**
 * Get quality color for UI feedback
 */
export function getQualityColor(score: number): string {
  if (score >= 80) return "#22c55e"; // Green
  if (score >= 60) return "#eab308"; // Yellow
  return "#ef4444"; // Red
}

/**
 * Get quality message for display
 */
export function getQualityMessage(score: number): string {
  if (score >= 80) return "Excellent - Ready to capture";
  if (score >= 60) return "Good - Can capture";
  if (score >= 40) return "Fair - Improve if possible";
  return "Poor - Please adjust";
}
